import { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { supabase } from '../../lib/supabase'
import { DEFAULT_LOGIN_THEME, isChristmasLoginActive, LOGIN_THEME_SETTING_KEY, normalizeLoginTheme, type LoginThemeMode } from '../../lib/loginTheme'

function dateInputValue(iso: string | null) {
  if (!iso) return ''
  const value = new Date(iso)
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 16)
}
function toIso(input: string) {
  const time = new Date(input)
  return Number.isFinite(time.getTime()) ? time.toISOString() : null
}
export function LoginThemeAdmin() {
  const { session, profile } = useAuth()
  const [mode, setMode] = useState<LoginThemeMode>('off')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [preview, setPreview] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [published, setPublished] = useState(DEFAULT_LOGIN_THEME)

  async function refresh() {
    setLoading(true)
    const { data, error: readError } = await supabase.from('app_settings').select('value').eq('key', LOGIN_THEME_SETTING_KEY).maybeSingle()
    if (readError) {
      setError('Não foi possível carregar a configuração do tema.')
    } else {
      const config = normalizeLoginTheme(data?.value)
      setPublished(config)
      setMode(config.mode)
      setStartsAt(dateInputValue(config.startsAt))
      setEndsAt(dateInputValue(config.endsAt))
      setError('')
    }
    setLoading(false)
  }
  useEffect(() => { if (session && profile?.role === 'administrador') void refresh() }, [session?.user?.id, profile?.role])

  const proposed = normalizeLoginTheme({ mode, startsAt: toIso(startsAt), endsAt: toIso(endsAt) })
  const invalidDates = mode === 'scheduled' && (!startsAt || !endsAt || !toIso(startsAt) || !toIso(endsAt) || new Date(startsAt).getTime() >= new Date(endsAt).getTime())
  const current = isChristmasLoginActive(published)

  async function save() {
    if (!session || profile?.role !== 'administrador' || invalidDates) return
    setSaving(true)
    setError('')
    setMessage('')
    const { error: updateError } = await supabase.from('app_settings').upsert({
      key: LOGIN_THEME_SETTING_KEY,
      value: JSON.stringify(proposed),
      updated_by: session.user.id,
    }, { onConflict: 'key' })
    if (updateError) {
      setError('Falha ao salvar. Verifique a migração SQL e as permissões do administrador.')
    } else {
      setPublished(proposed)
      setMessage('Configuração publicada. O login atualizará o tema na próxima consulta.')
    }
    setSaving(false)
  }

  return (
    <section className="admin-login-theme" aria-labelledby="admin-login-theme-title" style={{marginTop:32}}>
      <div className="admin-section-title"><div>
        <p className="eyebrow">PERSONALIZAÇÃO SAZONAL</p>
        <h2 id="admin-login-theme-title">Tema da tela de acesso</h2>
        <p className="muted">Controle o visual do login sem alterar autenticação, senhas, permissões ou módulos.</p>
      </div><span>{current ? 'Natal em exibição' : 'Login normal em exibição'}</span></div>
      {error && <div className="notice error" role="alert">{error}</div>}
      {message && <div className="notice success" role="status">{message}</div>}
      <div style={{display:'grid',gap:18,gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,330px),1fr))'}}>
        <div className="visual-template-card">
          <label htmlFor="login-theme-mode"><strong>Modo de exibição</strong></label>
          <select id="login-theme-mode" value={mode} onChange={event=>setMode(event.target.value as LoginThemeMode)} disabled={loading||saving} style={{padding:12,border:'1px solid #cdd8e5',borderRadius:10,width:'100%'}}>
            <option value="off">Normal — desativado</option>
            <option value="on">Natal — ativar agora</option>
            <option value="scheduled">Natal — agendar período</option>
          </select>
          {mode==='scheduled' && <div style={{display:'grid',gap:10}}>
            <label>Início (horário local)<input aria-label="Início do tema" type="datetime-local" value={startsAt} onChange={event=>setStartsAt(event.target.value)} style={{display:'block',width:'100%',padding:10}} /></label>
            <label>Fim (horário local)<input aria-label="Fim do tema" type="datetime-local" value={endsAt} onChange={event=>setEndsAt(event.target.value)} style={{display:'block',width:'100%',padding:10}} /></label>
            {invalidDates && <div className="notice error" role="alert">Informe início e fim válidos. O término deve ser posterior ao início.</div>}
          </div>}
          <p className="muted">O modo manual prevalece sobre o agendamento. A configuração vale para todos os usuários e permanece auditada.</p>
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            <button className="secondary-button" type="button" onClick={()=>setPreview(v=>!v)}>{preview?'Ocultar prévia':'Pré-visualizar Natal'}</button>
            <button className="primary-button" type="button" disabled={loading||saving||invalidDates||!session} onClick={()=>void save()}>{saving?'Salvando...':'Publicar configuração'}</button>
          </div>
        </div>
        <div className="visual-template-card">
          <strong>Estado publicado</strong>
          <p>{current?'🎄 O tema natalino está ativo.':'O login padrão está ativo.'}</p>
          <p className="muted">Modo: {published.mode==='off'?'Desativado':published.mode==='on'?'Ativação manual':'Agendado'}</p>
          {published.mode==='scheduled'&&<p className="muted">Período: {new Date(published.startsAt!).toLocaleString('pt-BR')} até {new Date(published.endsAt!).toLocaleString('pt-BR')}</p>}
          <p className="muted">Na ausência da configuração ou em caso de erro de leitura, a tela normal é exibida. Alterações são consultadas a cada minuto e quando a aba volta a ficar visível.</p>
          <button className="secondary-button" type="button" onClick={()=>void refresh()} disabled={loading||saving}>Atualizar estado</button>
        </div>
      </div>
      {preview && (
        <div style={{marginTop:20,padding:14,border:'1px solid #dce5ef',borderRadius:14,background:'#f7f9fc'}}>
          <p style={{fontSize:12,margin:'0 0 12px',color:'#52647d'}}><strong>Prévia ilustrativa do tema</strong> — somente para esta sessão, sem publicar.</p>
          <div style={{display:'grid',gridTemplateColumns:'53% 47%',width:'100%',aspectRatio:'16/8',maxHeight:430,overflow:'hidden',borderRadius:10,boxShadow:'0 8px 20px #15335b25'}}>
            <div style={{position:'relative',background:'#061b40',color:'#fff',padding:'6%',display:'flex',flexDirection:'column',justifyContent:'center',overflow:'hidden'}}>
              <img src="/assets/login/christmas/bg-natal-esquerda.jpg" alt="" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',objectPosition:'center bottom',pointerEvents:'none'}} />
              <div style={{position:'relative',zIndex:1,textShadow:'0 1px 5px #0b1f4d'}}>
                <strong style={{display:'block',fontSize:'clamp(12px,2vw,28px)',maxWidth:340,lineHeight:1.13}}>Informação certa para um atendimento mais simples.</strong>
                <p style={{fontSize:'clamp(7px,.8vw,12px)',maxWidth:330}}>Um ambiente único para consultar procedimentos, padronizar orientações e apoiar a operação.</p>
              </div>
              <span style={{position:'absolute',bottom:8,left:12,right:12,zIndex:2,background:'#041430dd',padding:6,fontSize:9}}>Águas Guariroba · Suite de Procedimentos</span>
            </div>
            <div style={{position:'relative',background:'#fff',padding:'7%',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}>
              <img src="/assets/login/christmas/pinheiro-laco.png" alt="" style={{position:'absolute',bottom:0,right:0,width:'25%',pointerEvents:'none'}} />
              <div style={{position:'relative',zIndex:1,maxWidth:260,width:'100%'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                  <img src="/assets/login/christmas/azevinho.png" alt="" style={{height:32,maxWidth:115,objectFit:'contain'}} />
                  <div style={{height:1,background:'#c8a352',flex:1}} />
                </div>
                <strong style={{display:'block',fontSize:'clamp(10px,1.5vw,21px)',color:'#102b55'}}>Bem-vindo à Suite</strong>
                <span style={{display:'inline-block',background:'#B3121F',color:'#fff',fontSize:9,borderRadius:999,padding:'3px 9px',margin:'5px 0 10px'}}>🎁 Edição de Natal</span>
                <div style={{height:24,background:'#e7eef9',borderRadius:6,marginBottom:8}} />
                <div style={{height:24,background:'#e7eef9',borderRadius:6,marginBottom:8}} />
                <div style={{height:26,background:'linear-gradient(90deg,#0B1F4D,#123A7A)',borderRadius:6,color:'#fff',fontSize:9,display:'grid',placeItems:'center'}}>Entrar na Suite →</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
